import camelcaseKeys from 'camelcase-keys'
import async from 'async'

import ApiClient from '@src/lib/api_client'
import { API } from '@src/lib/api_path'
import { MemberTrainingPlanDTO, TrainingPlanDTO } from '@src/routes/content/components/training-plan-builder/save-plan'
import { snakecaseKeys } from '@src/util/functions'
import TrainingPlan, { TrainingDay } from '@src/data/training-plan'
import EventBus from '@src/lib/event_bus'
import SessionManager from '@src/lib/session_manager'
import { convertLocalDateToDate, convertToLocalDate } from '@src/util/date-format'

class ErrorResponse {
  errors: {
    intersections: any[]
    training_days: {}
  }

  constructor (attributes?) {
    Object.assign(this, attributes)
  }
}

export type AssignError = {
  intersections: TrainingPlan[]
  trainingPlanData: MemberTrainingPlanDTO,
  training_days: Object
}

type PaginatedTrainingPlansResponse = {
  meta: {
    current_page: number,
    total_pages: number,
    total_club_training_plans: number | null,
    total_member_training_plans: number | null
  },
  training_plans: TrainingPlan[]
}

/**
 * Service utility dealing with training plans.
 */
export class TrainingPlanService {

  apiClient: ApiClient = new ApiClient()

  static buildMemberOwnedPlan (trainingPlanName: string, startDate: string, trainingDays: any[], memberId: number, visible: boolean): MemberTrainingPlanDTO {
    return new MemberTrainingPlanDTO({
      name: trainingPlanName,
      holderId: memberId,
      trainingDays: trainingDays,
      startDate: startDate,
      visible
    })
  }

  static computeStartDate (endDate: string, planTotalDays: number): string {
    let selectedDate = convertLocalDateToDate(endDate)
    selectedDate.setDate(selectedDate.getDate() - planTotalDays + 1)
    return convertToLocalDate(selectedDate)
  }

  static computeEndDate (startDate: string, planTotalDays: number): string {
    let selectedDate = convertLocalDateToDate(startDate)
    selectedDate.setDate(selectedDate.getDate() + planTotalDays - 1)
    return convertToLocalDate(selectedDate)
  }

  assignTrainingPlanToMember (trainingPlanName: string, startDate: string, trainingDays: any[], memberId: number, visible: boolean): Promise<AssignError> {
    const trainingPlanData = TrainingPlanService.buildMemberOwnedPlan(trainingPlanName, startDate, trainingDays, memberId, visible)

    return this.apiClient.post(SessionManager.Instance.isManager() ? API.TRAINERS_API.TRAINING_PLANS : API.MEMBERS_API.TRAINING_PLANS, { training_plan: snakecaseKeys(trainingPlanData) })
      .then(() => {
        return { intersections: [], trainingPlanData: trainingPlanData, training_days: {} } as AssignError
      })
      .catch((error) => {
        const assignError = new ErrorResponse(error.errorData)
        if (assignError.errors.training_days) {
          return {
            training_days: assignError.errors.training_days,
            trainingPlanData: trainingPlanData,
            intersections: []
          } as AssignError
        }
        return {
          intersections: assignError.errors.intersections.map(trainingPlan => new TrainingPlan(camelcaseKeys(trainingPlan, { deep: true }))),
          trainingPlanData: trainingPlanData,
          training_days: {}
        } as AssignError
      })
  }

  overwriteExistingTrainingPlans (intersections: TrainingPlan[], trainingPlanData: MemberTrainingPlanDTO, callback, store) {

    // sync: delete training plans, post/put training plan

    async.waterfall(
      intersections.map((intersectionTrainingPlan) => {
        return (done) => {
          // same execution for each item in the array
          return this.apiClient.delete(`${SessionManager.Instance.isManager() ? API.TRAINERS_API.TRAINING_PLANS : API.MEMBERS_API.TRAINING_PLANS}/${intersectionTrainingPlan.id}`)
          // results carried along from each to the next
            .then(() => done(null))

        }
      }).concat([(done) => {
        if (trainingPlanData.id) {
          return this.apiClient.put(`${SessionManager.Instance.isManager() ? API.TRAINERS_API.TRAINING_PLANS : API.MEMBERS_API.TRAINING_PLANS}/${trainingPlanData.id}`, { training_plan: snakecaseKeys(trainingPlanData) })
            .then(() => {
              store.dispatch('setCheckupTrainingPlan', trainingPlanData.id)
            })
            .finally(() => done(true))
        } else {
          return this.apiClient.post(SessionManager.Instance.isManager() ? API.TRAINERS_API.TRAINING_PLANS : API.MEMBERS_API.TRAINING_PLANS, { training_plan: snakecaseKeys(trainingPlanData) })
            .then((response: { training_plan: TrainingPlan }) => {
              store.dispatch('setCheckupTrainingPlan', response.training_plan.id)
            })
            .finally(() => done(true))
        }
      }]),
      (finalResult) => {
        // final callback
        // the last task always finishes with true, so there is no else path
        /* istanbul ignore else */
        if (finalResult) {
          EventBus.trigger('reloadSelectedMember')
        }
        callback(finalResult)
      })
  }

  getTrainingPlans (page, itemsPerPage, scope: string, name: string | undefined, memberId: number | undefined = undefined, history: boolean = false, upcoming: boolean = false): Promise<any> {
    const apiPath = SessionManager.Instance.isManager() ?
      API.TRAINERS_API.MEMBER_TRAINING_PLANS : API.MEMBERS_API.MEMBER_TRAINING_PLANS
    let params = {
      scope: scope,
      history,
      upcoming,
      page: page,
      items_per_page: itemsPerPage
    }
    if (memberId !== undefined) params['member_id'] = memberId
    if (name !== undefined) params['name'] = name

    return this.apiClient.get<PaginatedTrainingPlansResponse>(apiPath, params)
      .then(response => {
        return Promise.resolve({
          trainingPlans: response.training_plans.map((trainingPlan) => new TrainingPlan(camelcaseKeys(trainingPlan, { deep: true }))),
          totalPages: response.meta.total_pages,
          noOfClubTrainings: response.meta.total_club_training_plans,
          onOfMemberTrainings: response.meta.total_member_training_plans
        })
      })
      .catch(error => {
        console.error('failed to retrieve training plans', error)
        return Promise.reject(error)
      })
  }

  getTrainingPlan (id): Promise<TrainingPlan | null> {
    return this.apiClient.get<any>(`${SessionManager.Instance.isManager() ? API.TRAINERS_API.TRAINING_PLANS : API.MEMBERS_API.TRAINING_PLANS}/${id}`)
      .then((response) => {
        return Promise.resolve(new TrainingPlan(camelcaseKeys(response.training_plan)))
      })
      .catch(err => {
        console.error('failed to retrieve training plan', err)
        return Promise.reject(err)
      })
  }

  getTrainingPlanWithDays (id): Promise<TrainingPlan | null> {
    return this.apiClient.get<any>(`${SessionManager.Instance.isManager() ? API.TRAINERS_API.TRAINING_PLANS : API.MEMBERS_API.TRAINING_PLANS}/${id}`)
      .then((response) => {
        let trainingPlan = response.training_plan
        let trainingDays: TrainingDay[] = []
        response.training_plan.training_days.forEach(day => {
          trainingDays.push(new TrainingDay(camelcaseKeys(day, { deep: true })))
        })
        trainingPlan.trainingDays = trainingDays || /* istanbul ignore next: trainingDays is always an array */ []
        return Promise.resolve(new TrainingPlan(camelcaseKeys(trainingPlan)))
      })
      .catch(err => {
        console.error('failed to retrieve training plan', err)
        return Promise.reject(err)
      })
  }

  getTrainingPlanDays (id): Promise<TrainingDay[] | null> {
    return this.apiClient.get<any>(`${SessionManager.Instance.isManager() ? API.TRAINERS_API.TRAINING_PLANS : API.MEMBERS_API.TRAINING_PLANS}/${id}`)
      .then((response) => {
        let trainingDays: TrainingDay[] = []
        response.training_plan.training_days.forEach(day => {
          trainingDays.push(new TrainingDay(camelcaseKeys(day, { deep: true })))
        })
        return Promise.resolve(trainingDays)
      })
  }

  delete (id): Promise<void | null> {
    return this.apiClient.delete<any>(`${SessionManager.Instance.isManager() ? API.TRAINERS_API.TRAINING_PLANS : API.MEMBERS_API.TRAINING_PLANS}/${id}`)
      .then(() => Promise.resolve())
      .catch(error => {
        console.error('failed to remove training plan', error)
        return Promise.reject((error))
      })
  }

  addPlan (plan: TrainingPlanDTO): Promise<void> {
    return this.apiClient.post(SessionManager.Instance.isManager() ? API.TRAINERS_API.TRAINING_PLANS : API.MEMBERS_API.TRAINING_PLANS, { training_plan: snakecaseKeys(plan) })
      .then(() => Promise.resolve())
      .catch((error) => Promise.reject(error))
  }

  editPlan (trainingPlanId: number, newPlan: TrainingPlanDTO): Promise<void> {
    return this.apiClient.put(`${SessionManager.Instance.isManager() ? API.TRAINERS_API.TRAINING_PLANS : API.MEMBERS_API.TRAINING_PLANS}/${trainingPlanId}`, { training_plan: snakecaseKeys(newPlan) })
      .then(() => Promise.resolve())
      .catch((error) => Promise.reject(error))
  }

  addMemberOwnedPlan (plan: MemberTrainingPlanDTO): Promise<number | null> {
    return this.apiClient.post(SessionManager.Instance.isManager() ? API.TRAINERS_API.TRAINING_PLANS : API.MEMBERS_API.TRAINING_PLANS, { training_plan: snakecaseKeys(plan) })
      .then((response: { training_plan: TrainingPlan }) => Promise.resolve(response.training_plan.id))
      .catch(error => Promise.reject(error))
  }

  editActiveTrainingPlan (trainingPlanId: number, newPlan: MemberTrainingPlanDTO): Promise<void> {
    return this.apiClient.put(`${SessionManager.Instance.isManager() ? API.TRAINERS_API.TRAINING_PLANS : API.MEMBERS_API.TRAINING_PLANS}/${trainingPlanId}`, { training_plan: snakecaseKeys(newPlan) })
      .then(() => Promise.resolve())
      .catch(error => Promise.reject(error))
  }
}
